// src/store/configSlice.js
import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from "@reduxjs/toolkit";
import { loadClientConfig } from "../services/firebase";

/**
 * Async thunk to load client configuration from Firestore
 * @param {string} clientSlug - The client's URL slug
 */
export const fetchClientConfig = createAsyncThunk(
  "config/fetchClientConfig",
  async (clientSlug, { rejectWithValue }) => {
    try {
      const config = await loadClientConfig(clientSlug);
      if (!config) {
        return rejectWithValue(`Client "${clientSlug}" not found`);
      }
      return config;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

const initialState = {
  clientSlug: null,
  clientConfig: null,
  activeMapId: null,
  loading: false,
  error: null,
  activeState: null,
};

const configSlice = createSlice({
  name: "config",
  initialState,
  reducers: {
    setActiveMap: (state, action) => {
      const mapId = action.payload;
      const map = state.clientConfig?.maps?.find((m) => m.id === mapId);

      if (map) {
        state.activeMapId = mapId;
        const stateConfig = state.clientConfig?.states?.find(
          (s) => s.abbreviation === map.stateAbbreviation,
        );
        state.activeState = stateConfig || null;
      }
    },

    clearConfig: (state) => {
      state.clientSlug = null;
      state.clientConfig = null;
      state.activeMapId = null;
      state.activeState = null;
      state.loading = false;
      state.error = null;
    },

    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClientConfig.fulfilled, (state, action) => {
        state.clientConfig = action.payload;
        state.clientSlug = action.payload.clientSlug;
        state.loading = false;

        // Set default active map
        const defaultMap =
          action.payload.maps?.find((m) => m.default) ||
          action.payload.maps?.[0];

        if (defaultMap) {
          state.activeMapId = defaultMap.id;
          const stateConfig = action.payload.states?.find(
            (s) => s.abbreviation === defaultMap.stateAbbreviation,
          );
          state.activeState = stateConfig || null;
        }
      })
      .addCase(fetchClientConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load client configuration";
      });
  },
});

export const { setActiveMap, clearConfig, clearError } = configSlice.actions;

// ============================================
// BASIC SELECTORS
// ============================================

export const selectClientSlug = (state) => state.config.clientSlug;
export const selectClientConfig = (state) => state.config.clientConfig;
export const selectConfigLoading = (state) => state.config.loading;
export const selectConfigError = (state) => state.config.error;
export const selectActiveMapId = (state) => state.config.activeMapId;
export const selectActiveState = (state) => state.config.activeState;

// ============================================
// MEMOIZED SELECTORS
// ============================================

export const selectClientInfo = createSelector(
  [selectClientConfig],
  (config) => config?.client || null,
);

export const selectAvailableStates = createSelector(
  [selectClientConfig],
  (config) => config?.states || [],
);

export const selectAvailableMaps = createSelector(
  [selectClientConfig],
  (config) => config?.maps || [],
);

export const selectActiveMap = createSelector(
  [selectAvailableMaps, selectActiveMapId],
  (maps, activeMapId) => maps.find((m) => m.id === activeMapId) || null,
);

export const selectMapsByState = createSelector(
  [selectAvailableMaps],
  (maps) => {
    const grouped = {};
    maps.forEach((map) => {
      const state = map.stateAbbreviation;
      if (!grouped[state]) {
        grouped[state] = [];
      }
      grouped[state].push(map);
    });
    return grouped;
  },
);

export const selectMapsForActiveState = createSelector(
  [selectAvailableMaps, selectActiveState],
  (maps, activeState) => {
    if (!activeState) return [];
    return maps.filter((m) => m.stateAbbreviation === activeState.abbreviation);
  },
);

export const selectActiveTierCutoffs = createSelector(
  [selectActiveMap],
  (activeMap) => activeMap?.tierCutoffs || null,
);

export const selectActiveDisplayConfig = createSelector(
  [selectActiveMap],
  (activeMap) => activeMap?.display || null,
);

export const selectActivePopupConfig = createSelector(
  [selectActiveMap],
  (activeMap) => activeMap?.popup || null,
);

export const selectActiveDetailsConfig = createSelector(
  [selectActiveMap],
  (activeMap) => activeMap?.details || null,
);

export const selectFeatures = createSelector(
  [selectClientConfig],
  (config) => config?.features || {},
);

export const selectActiveGeographyType = createSelector(
  [selectActiveMap],
  (activeMap) => activeMap?.geographyType || null,
);

export const selectActiveGeographyLabels = createSelector(
  [selectActiveMap],
  (activeMap) => ({
    singular: activeMap?.geographyLabel || "Geography",
    plural: activeMap?.geographyLabelPlural || "Geographies",
  }),
);

// ============================================
// V2 AVATAR SELECTORS
// ============================================

/**
 * Select all avatars from config
 */
export const selectAvatars = createSelector(
  [selectClientConfig],
  (config) => config?.avatars || [],
);

/**
 * Select a specific avatar by ID
 */
export const selectAvatarById = createSelector(
  [selectAvatars, (_, avatarId) => avatarId],
  (avatars, avatarId) => avatars.find((a) => a.id === avatarId) || null,
);

/**
 * Select the avatar for the currently active map
 * Replaces the old selectActiveScoringAlgorithm
 */
export const selectActiveAvatar = createSelector(
  [selectActiveMap, selectAvatars],
  (activeMap, avatars) => {
    if (!activeMap?.avatarId) return null;
    return avatars.find((a) => a.id === activeMap.avatarId) || null;
  },
);

/**
 * Backward-compat: selectActiveScoringAlgorithm now returns avatar data
 * shaped like the old scoringAlgorithm object for any code that still reads it.
 */
export const selectActiveScoringAlgorithm = createSelector(
  [selectActiveAvatar],
  (avatar) => {
    if (!avatar) return null;
    return {
      id: avatar.id,
      name: avatar.name,
      description: avatar.description,
    };
  },
);

// ============================================
// V2 REPORT SELECTORS
// ============================================

/**
 * Select global reports (e.g. FAQ) from config
 */
export const selectGlobalReports = createSelector(
  [selectClientConfig],
  (config) => config?.globalReports || [],
);

/**
 * Select reports for the active map (rankedPdf, rankedXlsx, executiveSummary)
 */
export const selectActiveMapReports = createSelector(
  [selectActiveMap],
  (activeMap) => activeMap?.reports || null,
);

/**
 * Select reports for the active avatar (methodology)
 */
export const selectActiveAvatarReports = createSelector(
  [selectActiveAvatar],
  (avatar) => avatar?.reports || null,
);

/**
 * Select all reports structured by avatar → maps.
 * Used by ReportsView to render the full report hierarchy.
 *
 * Returns: {
 *   avatars: [
 *     {
 *       avatar: { id, name, description },
 *       avatarReports: { methodology: {...} },
 *       maps: [
 *         {
 *           map: { id, label, geographyType, stateAbbreviation, ... },
 *           mapReports: { rankedPdf: {...}, rankedXlsx: {...}, executiveSummary: {...} },
 *         },
 *       ],
 *     },
 *   ],
 *   globalReports: [...],
 * }
 */
export const selectStructuredReports = createSelector(
  [selectAvatars, selectAvailableMaps, selectGlobalReports],
  (avatars, maps, globalReports) => {
    const structured = avatars.map((avatar) => {
      const avatarMaps = maps
        .filter((m) => m.avatarId === avatar.id)
        .map((map) => ({
          map: {
            id: map.id,
            label: map.label,
            geographyType: map.geographyType,
            geographyLabel: map.geographyLabel,
            stateAbbreviation: map.stateAbbreviation,
          },
          mapReports: map.reports || {},
        }));

      return {
        avatar: {
          id: avatar.id,
          name: avatar.name,
          description: avatar.description,
        },
        avatarReports: avatar.reports || {},
        maps: avatarMaps,
      };
    });

    return {
      avatars: structured,
      globalReports,
    };
  },
);

/**
 * Backward-compat: selectReports returns a flat array aggregated from v2 structure.
 * Any old code reading config.reports[] will get a flattened list.
 * New code should use selectStructuredReports instead.
 */
export const selectReports = createSelector(
  [selectStructuredReports],
  (structured) => {
    const flat = [];

    // Global reports
    structured.globalReports.forEach((r) => {
      flat.push({ ...r, category: "global" });
    });

    // Avatar and map reports
    structured.avatars.forEach(({ avatar, avatarReports, maps }) => {
      // Methodology
      if (avatarReports.methodology) {
        flat.push({
          ...avatarReports.methodology,
          id: `${avatar.id}_methodology`,
          category: "avatar",
          avatarId: avatar.id,
          avatarName: avatar.name,
        });
      }

      // Per-map reports
      maps.forEach(({ map, mapReports }) => {
        Object.entries(mapReports).forEach(([key, report]) => {
          flat.push({
            ...report,
            id: `${map.id}_${key}`,
            category: "map",
            mapId: map.id,
            mapLabel: map.label,
            geographyType: map.geographyType,
            avatarId: avatar.id,
            avatarName: avatar.name,
          });
        });
      });
    });

    return flat;
  },
);

/**
 * Derive storage paths for the active map
 * Includes TopoJSON object name for conversion
 */
export const selectActiveDataPaths = createSelector(
  [selectActiveMap, selectClientSlug],
  (activeMap, clientSlug) => {
    if (!activeMap || !clientSlug) return null;

    const stateAbbrev = activeMap.stateAbbreviation;
    const geoType = activeMap.geographyType;
    const topoObjectName = activeMap.data?.topoJsonObjectName || "zcta";

    return {
      // Shared data paths
      demographics: `shared/demographics/${geoType}/${stateAbbrev}.json`,
      geoJson:
        activeMap.data?.topoJsonPath ||
        `shared/geojson/${geoType}/${stateAbbrev}.json`,

      // Client-specific data path
      scoredData: `clients/${clientSlug}/scored/${activeMap.data.scoredDataPath}`,

      // Competitor data path (optional — only present if competitorsPath is configured)
      competitors: activeMap.data?.competitorsPath
        ? `clients/${clientSlug}/${activeMap.data.competitorsPath}`
        : null,

      // Opportunity data path (optional — only present if opportunitiesPath is configured)
      opportunities: activeMap.data?.opportunitiesPath
        ? `clients/${clientSlug}/${activeMap.data.opportunitiesPath}`
        : null,

      // TopoJSON object name for conversion
      topoObjectName,
    };
  },
);

export default configSlice.reducer;
